import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { BiDownload, BiTrash } from "react-icons/bi";
import { makeStyles } from "@mui/styles";
import DocumentUpload from "components/UI/documentUpload/DocumentUpload";
import { textStyle } from "components/UI/dynamicTable/DynamicTable";
import AdvancedInputField from "components/UI/AdvancedInputField/AdvancedInputField";

export const useScrollbarStyles = makeStyles((theme) => ({
  container: {
    border: `1px solid var(--scroll-box-border-color)`,
    borderRadius: 8,

    overflowX: "auto",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 10,
      height: 10,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#fbfbfb",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#e9e9e9",
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#bbbbbb",
    },
  },
}));

const MatrixType = ({
  question,
  options,
  submittedAnswer,
  responseAnswer,
  isFrom,
  handleQuesAnswer,
  questionType,
  event,
  comment,
  setTableHeader,
  tableHeadersApi,
  tableRowsApi,
  linkageAnswer,
  setTableRow,
  isActive,
  hasAttachedDocument,
}) => {
  const classes = useScrollbarStyles();
  const [headers, setHeaders] = useState([]);
  const [types, setTypes] = useState([]);
  const [category, setCategory] = useState([]);
  const [row, setRow] = useState(() => {
    const initialState = tableRowsApi?.map((rowItem) => [
      { option: rowItem?.title, type: "rowHeader" },
      ...tableHeadersApi?.map((item) => ({
        option: "",
        weight: 2,
        documentUrl: null,
        comment: null,
        type: null,
      })),
    ]);
    return initialState;
  });
  const [defineRowHeaders, setDefineRowHeaders] = useState([]);
  const [commentBox, setCommentBox] = useState("");
  const [dropdownOption, setDropdownOption] = useState({});
  const [attachDocument, setAttachDocument] = useState(null);

  // const [modalOpen, setModalOpen] = useState(false);
  // const pdfUrl = "https://example.com/path/to/your/pdf.pdf";

  const inputRefs = useRef({});
  const checkClickRef = useRef(null);

  const columnTypes = [
    // "Document",
    "Text",
    "Number",
    "Url",
    "Email",
    "Dropdown",
  ];

  const onTypesChange = (newTypes) => {
    setTypes(newTypes);
  };

  const onHeadersChange = (newHeaders) => {
    setHeaders(newHeaders);
  };

  const handleHeaderChange = (event, index) => {
    const newHeaders = [...headers];
    newHeaders[index] = event.target.value;
    setHeaders(newHeaders);
    onHeadersChange(newHeaders);
    setCategory((prev) => {
      const newValue = [...category];
      newValue[index] = { ...category[index], title: event.target.value };

      return newValue;
    });
  };

  const handleTypeChange = (event, index) => {
    const newTypes = [...types];
    newTypes[index] = event.target.value;
    setTypes(newTypes);
    onTypesChange(newTypes);

    let modifiedType;
    if (event.target.value === "Text") {
      modifiedType = "TEXTFIELD";
    } else if (event.target.value === "Document") {
      modifiedType = "DOCUMENT_UPLOAD";
    } else if (event.target.value === "Email") {
      modifiedType = "EMAIL";
    } else if (event.target.value === "Number") {
      modifiedType = "NUMERIC_FIELD";
    } else if (event.target.value === "Url") {
      modifiedType = "URL";
    } else if (event.target.value === "Dropdown") {
      modifiedType = "DROPDOWN";
    }

    setCategory((prev) => {
      const newValue = [...category];
      if (event.target.value === "Dropdown") {
        newValue[index] = {
          ...category[index],
          type: modifiedType,
          options: [],
        };
      } else {
        const { options, ...rest } = category[index];
        newValue[index] = {
          ...rest,
          type: modifiedType,
        };
      }

      return newValue;
    });
  };

  const handleAddOption = (index) => {
    const value = dropdownOption[index]?.trim();
    if (value) {
      setCategory((prev) => {
        const newValue = [...prev];
        newValue[index] = {
          ...newValue[index],
          options: [...(newValue[index]?.options || []), { option: value }],
        };
        return newValue;
      });

      setDropdownOption((prev) => ({ ...prev, [index]: "" }));
    }
  };

  const handleRemoveOption = (headerIndex, optionIndex) => {
    setCategory((prev) => {
      const newValue = [...prev];
      newValue[headerIndex] = {
        ...newValue[headerIndex],
        options:
          newValue[headerIndex]?.options?.filter(
            (_, idx) => idx !== optionIndex
          ) || [],
      };
      return newValue;
    });
  };

  const handleOption = (event, index) => {
    setDropdownOption((prev) => ({
      ...prev,
      [index]: event.target.value,
    }));
  };

  const handleRowData = (
    event,
    cellIndex,
    rowIndex,
    type,
    dropdownOption,
    from = "document"
  ) => {
    if (from === "row") {
      const formattedValue =
        type === "Dropdown" ? "" : event.target.value.replace(/\D/g, "");
      const newData = [...row];

      if (type === "Document") {
        const customFile = new File(
          [event.target.files[0]],
          event.target.files[0].name,
          {
            type: event.target.files[0].type,
            lastModified: event.target.files[0].lastModified,
          }
        );

        customFile.docId = `${rowIndex},${cellIndex + 1}`;

        newData[rowIndex][cellIndex + 1] = {
          option: null,
          weight: 2,
          documentUrl: customFile,
          comment: null,
          type,
          docId: `${rowIndex},${cellIndex + 1}`,
        };
      } else if (type === "Dropdown") {
        newData[rowIndex][cellIndex + 1] = {
          option: dropdownOption || event.target.value,
          weight: 2,
          documentUrl: null,
          comment: null,
          type,
        };
      } else {
        newData[rowIndex][cellIndex + 1] = {
          option:
            type === "Number"
              ? formattedValue
              : type === "Email"
                ? event.target.value.toLowerCase()
                : event.target.value,
          weight: 2,
          documentUrl: null,
          comment: null,
          type,
        };
      }

      setRow(newData);
      handleQuesAnswer({
        obj: {
          rowData: newData,
          comment: commentBox,
          supporting_document: attachDocument,
        },
        // tableDocuments: customDocument,
        realQuestion: question,
      });
    } else if (from === "comment") {
      const { value } = event.target;
      setCommentBox(value);
      handleQuesAnswer({
        obj: {
          rowData: row,
          comment: value,
          supporting_document: attachDocument,
        },
        realQuestion: question,
      });
    } else if (from === "document") {
      setAttachDocument(cellIndex);
      handleQuesAnswer({
        obj: {
          rowData: row,
          comment: commentBox,
          supporting_document: cellIndex === null ? cellIndex : attachDocument,
          document: event === "document" ? cellIndex : attachDocument,
          documentMetaData:
            event === "document"
              ? {
                  ques_id: question?._id,
                  name: cellIndex?.name,
                  is_supporting_document: true,
                  type: cellIndex?.type,
                }
              : null,
        },
        realQuestion: question,
      });
    }
  };

  const handleFileInputClick = (rowIndex, cellIndex) => {
    const key = `${rowIndex}-${cellIndex}`;
    if (
      inputRefs.current[key] &&
      isFrom !== "approvalSection" &&
      isFrom !== "responseSection" &&
      isFrom !== "submissionSection"
    ) {
      inputRefs.current[key].current.click();
    }
  };

  const refScroll = useRef(null);

  const addHeader = () => {
    const newHeaders = [
      ...(headers || []),
      `Header ${headers?.length + 1 || 1}`,
    ];
    const newTypes = [...(types || []), "TEXTFIELD"];
    setHeaders(newHeaders);
    setTypes(newTypes);
    onHeadersChange(newHeaders);
    onTypesChange(newTypes);

    setCategory((prev) => {
      const newValue = [
        ...(category || []),
        {
          type: "TEXTFIELD",
          title: `Header ${headers?.length + 1}`,
        },
      ];

      return newValue;
    });
    setTimeout(() => {
      if (refScroll.current) {
        refScroll?.current?.scrollTo({
          left: refScroll.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }, 10);
  };

  useEffect(() => {
    if (!isActive) {
      refScroll?.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  }, [isActive]);

  const removeHeader = (index) => {
    const newHeaders = headers?.filter((_, i) => i !== index);
    const newTypes = types?.filter((_, i) => i !== index);
    setHeaders(newHeaders);
    setTypes(newTypes);
    onHeadersChange(newHeaders);
    onTypesChange(newTypes);

    setCategory((prev) => {
      const removeObj = category?.filter((_, i) => i !== index);
      return removeObj;
    });
  };

  // const addRow = () => {
  //   if (isFrom === "taskSection") {
  //     const rowData = Array.from({ length: tableHeadersApi?.length }, () => {
  //       return {
  //         option: "",
  //         weightage: 2,
  //         documentUrl: null,
  //         comment: null,
  //       };
  //     });

  //     const newRow = [...row, rowData];
  //     setRow(newRow);
  //   }
  // };

  const addRowHeaders = () => {
    setDefineRowHeaders([
      ...(defineRowHeaders || []),
      { title: `Row Header ${defineRowHeaders?.length + 1}` },
    ]);
  };

  const deleteRowHeaders = (index) => {
    const newDefineRowHeaders = defineRowHeaders.filter((_, i) => i !== index);
    setDefineRowHeaders(newDefineRowHeaders);
  };

  // const removeRow = (index) => {
  //   const remove = row?.filter((_, i) => i !== index);
  //   setRow(remove);
  // };

  useEffect(() => {
    if (isFrom === "surveyCreation") {
      setTableHeader(category);
      setTableRow(defineRowHeaders);
    }
  }, [category, defineRowHeaders]);

  useEffect(() => {
    setHeaders((prev) => tableHeadersApi?.map((header) => header?.title) || []);

    setTypes((prev) => tableHeadersApi?.map((header) => header?.type) || []);

    setDefineRowHeaders((prev) => tableRowsApi);

    setCategory(tableHeadersApi);

    // if (isFrom === "taskSection") {
    //   const rowData = Array.from({ length: tableRowsApi?.length }, () => {
    //     return Array.from({ length: tableHeadersApi?.length }, () => {
    //       return {
    //         option: null,
    //         weightage: 2,
    //         documentUrl: null,
    //         comment: null,
    //       };
    //     });
    //   });

    //   setRow(() => [rowData]);
    // }
  }, [tableHeadersApi, tableRowsApi]);

  const formattedType = (value) => {
    if (value === "TEXTFIELD") {
      value = "Text";
    } else if (value === "DOCUMENT_UPLOAD") {
      value = "Document";
    } else if (value === "EMAIL") {
      value = "Email";
    } else if (value === "NUMERIC_FIELD") {
      value = "Number";
    } else if (value === "URL") {
      value = "Url";
    } else if (value === "DROPDOWN") {
      value = "Dropdown";
    }
    return value;
  };

  const shortenFileName = (fileName, maxLength) => {
    if (fileName?.length <= maxLength) {
      return fileName;
    }

    const headLength = Math.floor((maxLength - 3) / 2);
    const tailLength = Math.ceil((maxLength - 3) / 2);

    const head = fileName?.substring(0, headLength);
    const tail = fileName?.substring(fileName?.length - tailLength);

    return fileName === undefined ? "-" : `${head}...${tail}`;
  };

  // useEffect(() => {
  //   if (isFrom === "taskSection") {
  //     const withCommentOption = row?.map((opt) =>
  //       opt.map((op) => {
  //         const { documentUrl, files, ...rest } = op || {};
  //         return {
  //           ...rest,
  //           files: files?.length > 0 ? files[0] : null,
  //           comment: commentBox,
  //         };
  //       })
  //     );

  //     console.log(withCommentOption)

  //     const withCommentAndDocOption = row?.map((opt) =>
  //       opt.map((op) => {
  //         // const { documentUrl, ...rest } = op || {};
  //         return {
  //           ...op,
  //           comment: commentBox,
  //         };
  //       })
  //     );

  //     console.log(withCommentAndDocOption)

  //     const customDocument = withCommentAndDocOption
  //       ?.flatMap((que) => que)
  //       ?.filter((q) => q?.documentUrl)
  //       ?.map((q) => q.documentUrl);

  //     handleQuesAnswer({
  //       obj: withCommentOption,
  //       tableDocuments: customDocument,
  //       realQuestion: question,
  //     });
  //   }
  // }, [row, commentBox]);

  useEffect(() => {
    setCommentBox(submittedAnswer?.comment);
    setAttachDocument(submittedAnswer?.supporting_document || null);

    if (submittedAnswer?.rowData?.length) {
      setRow(submittedAnswer?.rowData);
      const initialDropdowns = {};
      submittedAnswer?.rowData[0]?.forEach((cell, i) => {
        if (cell?.type === "Dropdown") {
          initialDropdowns[i] = cell.option;
        }
      });
      setDropdownOption(initialDropdowns);
    }
  }, []);

  const validateEmail = (eAddress) => {
    if (!eAddress) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(eAddress);
  };

  const validURL = (value) => {
    if (!value) return true;
    const urlRegex =
      /^((http(s?)?):\/\/)?([wW]{3}\.)?[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/g;
    return urlRegex.test(value);
  };

  // const handlePdfOpen = () => {
  //   setModalOpen(true);
  // };

  // const handlePdfClose = () => {
  //   setModalOpen(false);
  // };

  const placeholderSize = {
    inputProps: {
      sx: { fontSize: "14px" }, // Adjust placeholder font size
    },
  };

  const labelSize = {
    style: { fontSize: "14px" }, // Adjust label font size
  };

  useEffect(() => {
    if (isFrom === "surveyCreation") {
      if (isActive ? headers?.length === 0 : tableHeadersApi?.length === 0) {
        setHeaders(Array.from({ length: 3 }, (_, i) => `Header ${i + 1}`));
        setTypes(Array.from({ length: 3 }, (_, i) => "TEXTFIELD"));
        setCategory(
          Array.from({ length: 3 }, (_, i) => ({
            type: "TEXTFIELD",
            title: `Header ${i + 1}`,
          }))
        );
      }
      if (
        isActive ? defineRowHeaders?.length === 0 : tableRowsApi?.length === 0
      ) {
        setDefineRowHeaders(
          Array.from({ length: 2 }, (_, i) => ({
            title: `Row Header ${i + 1}`,
          }))
        );
      }
    }
  }, [tableHeadersApi, tableRowsApi]);

  return (
    <Box mt={2}>
      <TableContainer
      // style={{
      //   pointerEvents:
      //     isFrom === "approvalSection" ||
      //     isFrom === "responseSection" ||
      //     isFrom === "submissionSection" ||
      //     event,
      // }}
      >
        <Box className={classes.container} ref={refScroll}>
          {isFrom === "surveyCreation" && (
            <Typography variant="subtitle1" color="textSecondary">
              Define Column Headers
            </Typography>
          )}
          <Table
            sx={{
              pointerEvents: isFrom === "publishedSurvey" ? "none" : "auto",
              // borderCollapse: "collapse",

              // "& th, & td": {
              //   border: "1px solid #e0e0e0",
              // },
            }}
          >
            <TableHead>
              <TableRow style={{ display: "flex" }}>
                {isFrom !== "surveyCreation" && (
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor:
                        isFrom !== "surveyCreation" ? "#EBF1F8" : "",
                      // flex: 1,
                      width: "350px",
                    }}
                  ></TableCell>
                )}
                {headers?.map((header, index) => (
                  <TableCell
                    key={index}
                    style={{
                      backgroundColor:
                        isFrom !== "surveyCreation" ? "#EBF1F8" : "",
                      flex: 1,
                    }}
                  >
                    {isFrom === "surveyCreation" ? (
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          // overflow: "auto",
                        }}
                      >
                        <Box>
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <Box>
                              <TextField
                                value={header}
                                onChange={(event) =>
                                  handleHeaderChange(event, index)
                                }
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{
                                  marginRight: 8,
                                  pointerEvents:
                                    isFrom === "surveyCreation" && isActive
                                      ? "initial"
                                      : "none",
                                  minWidth: "300px",
                                }}
                                InputLabelProps={labelSize}
                                InputProps={placeholderSize}
                              />

                              <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                                style={{
                                  marginRight: 8,
                                  minWidth: 120,
                                  marginTop: 10,
                                }}
                              >
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={formattedType(types[index])}
                                  onChange={(event) =>
                                    handleTypeChange(event, index)
                                  }
                                  label="Type"
                                  inputProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                >
                                  {columnTypes?.map((type) => (
                                    <MenuItem
                                      key={type}
                                      value={type}
                                      style={{ fontSize: 14 }}
                                    >
                                      {type}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            {isActive && (
                              <IconButton
                                onClick={() => removeHeader(index)}
                                aria-label="delete"
                                size="medium"
                                color="primary"
                                style={{}}
                              >
                                <BiTrash />
                              </IconButton>
                            )}
                          </Box>

                          {formattedType(types[index]) === "Dropdown" &&
                            isActive && (
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={1}
                                mb={2}
                                mt={2}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  width={"89%"}
                                >
                                  <TextField
                                    value={dropdownOption[index] || ""}
                                    label={"Add option"}
                                    onChange={(event) =>
                                      handleOption(event, index)
                                    }
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    style={{
                                      // marginRight: 8,
                                      pointerEvents:
                                        isFrom === "surveyCreation"
                                          ? "initial"
                                          : "none",
                                    }}
                                  />
                                  <Button
                                    onClick={(event) => handleAddOption(index)}
                                    variant="contained"
                                  >
                                    +
                                  </Button>
                                </Box>
                                {category[index]?.options.length > 0 &&
                                  isActive &&
                                  category[index]?.options?.map(
                                    (item, optionIdx) => (
                                      <Chip
                                        key={index}
                                        label={item.option}
                                        onDelete={() =>
                                          handleRemoveOption(index, optionIdx)
                                        }
                                      />
                                    )
                                  )}
                              </Box>
                            )}
                        </Box>
                      </Box>
                    ) : (
                      <span style={{ marginLeft: "10px" }}>{header}</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {isFrom !== "surveyCreation" && (
              <TableBody>
                {(submittedAnswer?.rowData?.length > 0
                  ? submittedAnswer?.rowData
                  : defineRowHeaders
                )?.map((rowD, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <Box style={{ display: "flex" }}>
                      {submittedAnswer?.rowData?.length > 0 || (
                        <TableCell
                          key={rowIndex}
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                            width: "350px",
                            ...textStyle(),
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 500 }}
                          >
                            {rowD?.title}
                          </Typography>
                        </TableCell>
                      )}

                      {(submittedAnswer?.rowData?.length > 0
                        ? [{}, ...headers]
                        : headers
                      )?.map((header, cellIndex) => {
                        const key = `${rowIndex}-${cellIndex}`;
                        if (!inputRefs.current[key]) {
                          inputRefs.current[key] = React.createRef();
                        }
                        const adjustedIndex =
                          submittedAnswer?.rowData?.length > 0
                            ? cellIndex - 1
                            : cellIndex;

                        return (
                          <>
                            {cellIndex === 0 &&
                            submittedAnswer?.rowData?.length > 0 ? (
                              <TableCell
                                style={{ width: "300px", ...textStyle() }}
                                key={`${rowIndex}-${cellIndex}`}
                              >
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {rowD[cellIndex]?.option}
                                </Typography>
                              </TableCell>
                            ) : (
                              <TableCell
                                style={{ flex: 1, ...textStyle() }}
                                key={`${rowIndex}-${cellIndex}`}
                                onClick={() => {
                                  if (
                                    formattedType(
                                      types[
                                        submittedAnswer?.rowData?.length > 0
                                          ? cellIndex - 1
                                          : cellIndex
                                      ]
                                    ) === "Document"
                                  ) {
                                    handleFileInputClick(rowIndex, cellIndex);
                                  }
                                }}
                              >
                                {formattedType(
                                  types[
                                    submittedAnswer?.rowData?.length > 0
                                      ? cellIndex - 1
                                      : cellIndex
                                  ]
                                ) === "Dropdown" ? (
                                  isFrom === "taskSection" ||
                                  isFrom === "publishedSurvey" ? (
                                    <FormControl fullWidth>
                                      <InputLabel
                                        id="option-label"
                                        size="small"
                                        style={{ fontSize: "14px" }}
                                      >
                                        {isFrom === "approvalSection"
                                          ? "Selected option"
                                          : "Select option"}
                                      </InputLabel>

                                      <Select
                                        InputLabelProps={labelSize}
                                        InputProps={placeholderSize}
                                        labelId="option-label"
                                        value={rowD[cellIndex]?.option || ""}
                                        onChange={(event) =>
                                          handleRowData(
                                            event,
                                            submittedAnswer?.rowData?.length > 0
                                              ? cellIndex - 1
                                              : cellIndex,
                                            rowIndex,
                                            "Dropdown",
                                            event.target.value,
                                            "row"
                                          )
                                        }
                                        label={
                                          isFrom === "approvalSection"
                                            ? "Selected option"
                                            : "Select option"
                                        }
                                        size="small"
                                        style={{
                                          minWidth: "300px",
                                        }}
                                      >
                                        {category[
                                          submittedAnswer?.rowData?.length > 0
                                            ? cellIndex - 1
                                            : cellIndex
                                        ]?.options?.map((item, idx) => (
                                          <MenuItem
                                            key={idx}
                                            value={item.option}
                                          >
                                            {item.option}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography
                                      style={{
                                        width: "100%", // Adjust the width to fill the cell
                                      }}
                                      variant="body2"
                                    >
                                      {rowD[cellIndex] &&
                                      rowD[cellIndex]?.option !== null ? (
                                        rowD[cellIndex]?.option
                                      ) : (
                                        <span>&#8212;</span>
                                      )}
                                    </Typography>
                                  )
                                ) : formattedType(
                                    types[
                                      submittedAnswer?.rowData?.length > 0
                                        ? cellIndex - 1
                                        : cellIndex
                                    ]
                                  ) !== "Document" ? (
                                  isFrom === "taskSection" ||
                                  isFrom === "publishedSurvey" ? (
                                    <TextField
                                      type={
                                        formattedType(types[adjustedIndex]) ===
                                        "Number"
                                          ? "number"
                                          : "text"
                                      }
                                      value={rowD[cellIndex]?.option || ""}
                                      onChange={(event) =>
                                        handleRowData(
                                          event,
                                          adjustedIndex,
                                          rowIndex,
                                          formattedType(types[adjustedIndex]),
                                          "",
                                          "row"
                                        )
                                      }
                                      error={
                                        formattedType(types[adjustedIndex]) ===
                                          "Email" &&
                                        rowD[cellIndex]?.option &&
                                        !validateEmail(rowD[cellIndex]?.option)
                                          ? true
                                          : formattedType(
                                                types[adjustedIndex]
                                              ) === "Url" &&
                                              rowD[cellIndex]?.option &&
                                              !validURL(rowD[cellIndex]?.option)
                                            ? true
                                            : false
                                      }
                                      helperText={
                                        formattedType(types[adjustedIndex]) ===
                                          "Email" &&
                                        rowD[cellIndex]?.option &&
                                        !validateEmail(rowD[cellIndex]?.option)
                                          ? "Please enter a valid Email"
                                          : formattedType(
                                                types[adjustedIndex]
                                              ) === "Url" &&
                                              rowD[cellIndex]?.option &&
                                              !validURL(rowD[cellIndex]?.option)
                                            ? "Please enter a valid URL"
                                            : ""
                                      }
                                      variant="outlined"
                                      size="small"
                                      fullWidth
                                      style={{
                                        marginRight: 8,
                                        minWidth: "300px",
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      style={{
                                        width: "100%", // Adjust the width to fill the cell
                                      }}
                                      variant="body2"
                                    >
                                      {rowD[cellIndex] &&
                                      rowD[cellIndex]?.option !== null ? (
                                        rowD[cellIndex]?.option
                                      ) : (
                                        <span>&#8212;</span>
                                      )}
                                    </Typography>
                                  )
                                ) : (
                                  <Box
                                    style={{
                                      minWidth: "300px",
                                      cursor: "pointer",
                                      // height: "39px",
                                    }}
                                  >
                                    <input
                                      data-testid="file-input"
                                      name="actUpload"
                                      id="contained-button-activityFile"
                                      type="file"
                                      ref={inputRefs?.current[key]}
                                      onChange={(event) =>
                                        handleRowData(
                                          event,
                                          submittedAnswer?.rowData?.length > 0
                                            ? cellIndex - 1
                                            : cellIndex,
                                          rowIndex,
                                          formattedType(
                                            types[
                                              submittedAnswer?.rowData?.length >
                                              0
                                                ? cellIndex - 1
                                                : cellIndex
                                            ]
                                          ),
                                          "",
                                          "row"
                                        )
                                      }
                                      style={{
                                        display: "none",
                                        pointerEvents: event,
                                      }}
                                    />
                                    <Box
                                      style={{
                                        borderBottom:
                                          isFrom === "taskSection" ||
                                          isFrom === "publishedSurvey"
                                            ? "2px solid #ced4da"
                                            : "none",
                                        marginTop:
                                          isFrom === "taskSection" ||
                                          isFrom === "publishedSurvey"
                                            ? 6
                                            : 0,
                                        width: "100%",
                                      }}
                                    >
                                      {row?.[rowIndex]?.[
                                        submittedAnswer?.rowData?.length > 0
                                          ? cellIndex
                                          : cellIndex + 1
                                      ]?.documentUrl !== null ||
                                      rowD[cellIndex]?.files?.[0] !== null ? (
                                        row?.[rowIndex]?.[
                                          submittedAnswer?.rowData?.length > 0
                                            ? cellIndex
                                            : cellIndex + 1
                                        ]?.documentUrl?.name ||
                                        rowD?.[cellIndex]?.files?.[0]?.name ? (
                                          <Box>
                                            {isFrom === "publishedSurvey" ||
                                              isFrom === "surveyCreation" || (
                                                <Box
                                                  display={"flex"}
                                                  alignItems={"center"}
                                                >
                                                  <span>
                                                    {shortenFileName(
                                                      row?.[rowIndex]?.[
                                                        submittedAnswer?.rowData
                                                          ?.length > 0
                                                          ? cellIndex
                                                          : cellIndex + 1
                                                      ]?.documentUrl?.name ||
                                                        rowD?.[cellIndex]
                                                          ?.files?.[0]?.name,
                                                      isFrom === "taskSection"
                                                        ? 30
                                                        : 30
                                                    )}
                                                  </span>
                                                  {isFrom === "taskSection" || (
                                                    <a
                                                      href={
                                                        rowD[cellIndex]
                                                          ?.files?.[0]?.url
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        marginLeft: "10px",
                                                        cursor: "pointer",
                                                        width: "fit-content",
                                                      }}
                                                    >
                                                      <BiDownload size={24} />
                                                    </a>
                                                  )}
                                                </Box>
                                              )}
                                          </Box>
                                        ) : (
                                          "Upload file"
                                        )
                                      ) : isFrom === "taskSection" ||
                                        isFrom === "publishedSurvey" ? (
                                        "Upload file"
                                      ) : (
                                        <span>&#8212;</span>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </TableCell>
                            )}
                          </>
                        );
                      })}
                      {/* {isFrom === "taskSection" && tableHeadersApi?.length > 0 && (
                    <IconButton
                      onClick={() => removeRow(rowIndex)}
                      aria-label="delete"
                      size="small"
                      color="primary"
                      style={{ alignSelf: "center" }}
                      // style={{ marginTop: "24px" }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  )} */}
                    </Box>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </Box>
        {isFrom === "surveyCreation" && isActive && (
          <Box>
            <Button
              onClick={addHeader}
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              style={{ margin: "20px", width: "20%" }}
            >
              Add Column Headers
            </Button>
          </Box>
        )}
        {/* {isFrom === "taskSection" && (
          <Button
            onClick={addRow}
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            style={{ margin: "20px" }}
          >
            Add Row
          </Button>
        )} */}

        {isFrom === "surveyCreation" && (
          <Box>
            {isActive && <Divider />}
            <Typography variant="subtitle1" color="textSecondary" mt={2}>
              Define Row Headers
            </Typography>
            {defineRowHeaders?.map((defineRow, index) => (
              <Box>
                <TextField
                  style={{
                    width: "20%",
                    marginLeft: "20px",
                    marginTop: "15px",
                  }}
                  size="small"
                  variant="outlined"
                  value={defineRow?.title}
                  onChange={(e) => {
                    const newRowHeaders = [...defineRowHeaders];
                    newRowHeaders[index].title = e.target.value;
                    setDefineRowHeaders(newRowHeaders);
                  }}
                  placeholder="Enter row header title"
                  data-testid="row-header"
                  InputLabelProps={labelSize}
                  InputProps={placeholderSize}
                />
                {isActive && (
                  <IconButton
                    onClick={() => deleteRowHeaders(index)}
                    aria-label="delete"
                    size="medium"
                    color="primary"
                    //   style={{ alignSelf: "center" }}
                    style={{ marginTop: "15px", marginLeft: "5px" }}
                  >
                    <BiTrash />
                  </IconButton>
                )}
              </Box>
            ))}

            {isActive && (
              <Button
                onClick={addRowHeaders}
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                style={{ margin: "20px", width: "20%" }}
              >
                Add Row Headers
              </Button>
            )}
          </Box>
        )}
      </TableContainer>
      {comment && (
        <Box
          style={{
            pointerEvents: isFrom === "publishedSurvey" ? "none" : "auto",
            marginTop: "10px",
          }}
        >
          <AdvancedInputField
            name="comment"
            data={commentBox}
            placeholder="Contributor's comment"
            handleChange={({ name, value }) => {
              handleRowData({ target: { value } }, "", "", "", "", name);
            }}
          />
        </Box>
      )}
      {hasAttachedDocument && (
        <Box style={{ pointerEvents: event, marginTop: "10px" }}>
          <DocumentUpload
            value={attachDocument}
            isFrom="surveyTask"
            handleChange={handleRowData}
            question={"document"}
          />
        </Box>
      )}
    </Box>
  );
};

export default MatrixType;
